import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { agreementsApi } from '@/api';
import { AgreementMarkdown } from '@/components/AgreementMarkdown';
import { PublicAgreementView } from '@/types';

// Public, unauthenticated page for the buyer to read and sign the
// pre-inspection agreement. Mobile-friendly — most clients will sign on
// their phone after tapping the link in the email. Mirrors the chrome of
// ClientPortal at a high level (light surface, brand color, inspector
// footer) but is a single-purpose page with no nav.

const DEFAULT_BRAND = '#2F6A44';

const sanitizeHexColor = (raw: string | null | undefined): string => {
  if (!raw) return DEFAULT_BRAND;
  const trimmed = raw.trim();
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : DEFAULT_BRAND;
};

const SigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [agreement, setAgreement] = useState<PublicAgreementView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const sigRef = useRef<SignatureCanvas | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);

  // Track whether the canvas has any strokes — drives the Submit button
  // enabled state. SignaturePad provides isEmpty(), but we mirror it into
  // React state so the UI re-renders when the user starts/finishes drawing.
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await agreementsApi.getByToken(token);
        if (cancelled) return;
        if (resp.success && resp.data) {
          setAgreement(resp.data);
          if (resp.data.status === 'signed' && resp.data.signedPdfUrl) {
            setSignedPdfUrl(resp.data.signedPdfUrl);
          }
        } else {
          setLoadError('This signing link is not valid.');
        }
      } catch (err) {
        if (cancelled) return;
        const status =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : null;
        setLoadError(
          status === 404
            ? 'This signing link is not valid.'
            : 'Could not load the agreement. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleClear = () => {
    sigRef.current?.clear();
    setHasStrokes(false);
  };

  const handleSubmit = async () => {
    if (!token || !sigRef.current || sigRef.current.isEmpty()) {
      setSubmitError('Please sign in the box before submitting.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      // getTrimmedCanvas crops the white margin around the signature so the
      // resulting PNG is just the strokes — keeps the embedded signature
      // tight in the rendered PDF.
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      const resp = await agreementsApi.signByToken(token, dataUrl);
      if (resp.success && resp.data?.signedPdfUrl) {
        setSignedPdfUrl(resp.data.signedPdfUrl);
        setAgreement((prev) => (prev ? { ...prev, status: 'signed' } : prev));
      } else {
        setSubmitError('Something went wrong submitting your signature.');
      }
    } catch (err) {
      const backendMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: unknown } } }).response?.data?.error === 'string'
          ? ((err as { response: { data: { error: string } } }).response.data.error)
          : null;
      setSubmitError(backendMessage || 'Something went wrong submitting your signature.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-slate-500 text-sm">Loading agreement…</div>
      </div>
    );
  }

  if (loadError || !agreement) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Link not valid</h1>
          <p className="text-sm text-slate-600 mb-4">
            {loadError || 'This agreement is no longer available.'}
          </p>
          <Link to="/" className="text-sm text-slate-500 underline">
            Back to Flowspect
          </Link>
        </div>
      </div>
    );
  }

  const brand = sanitizeHexColor(agreement.brandColor);

  // Already-signed view — same component, different content. Lets the
  // client come back to the link and download the PDF without re-signing.
  if (agreement.status === 'signed' && signedPdfUrl) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: brand, color: 'white' }}
            >
              ✓
            </div>
            <h1 className="text-xl font-semibold text-slate-900">Already signed</h1>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            You signed this agreement on{' '}
            {agreement.signedAt
              ? new Date(agreement.signedAt).toLocaleString()
              : 'an earlier date'}
            . A confirmation email with the signed PDF was sent to {agreement.clientEmail}.
          </p>
          <a
            href={signedPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 rounded-md text-white text-sm font-medium"
            style={{ backgroundColor: brand }}
          >
            Open Signed Agreement
          </a>
        </div>
      </div>
    );
  }

  if (agreement.status === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Signing link expired</h1>
          <p className="text-sm text-slate-600">
            Please contact {agreement.companyName} to send a new signing link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          {agreement.logoUrl && (
            <img
              src={agreement.logoUrl}
              alt={agreement.companyName}
              className="max-h-12 mb-3"
            />
          )}
          <h1 className="text-2xl font-semibold text-slate-900">
            Pre-Inspection Agreement
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            from {agreement.inspectorName} · {agreement.companyName}
          </p>
        </div>

        {/* Agreement body */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8 mb-6">
          <p className="text-sm text-slate-600 mb-4">
            Please review the agreement below. Sign at the bottom and submit
            when you're ready.
          </p>
          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm text-slate-800 leading-relaxed">
              <AgreementMarkdown>{agreement.templateText}</AgreementMarkdown>
            </div>
          </div>
        </div>

        {/* Signature pad */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-slate-900">
              Signature
            </label>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              Clear
            </button>
          </div>
          {/*
            The canvas needs a finite intrinsic width for SignaturePad to
            initialize correctly. Wrapping in a relative container with a
            fixed height + full width gives it the layout it expects.
          */}
          <div
            className="relative w-full border border-slate-300 rounded-md bg-white"
            style={{ height: 200, touchAction: 'none' }}
          >
            <SignatureCanvas
              ref={(r) => {
                sigRef.current = r;
              }}
              penColor="#0f172a"
              canvasProps={{
                className: 'w-full h-full block',
                style: { width: '100%', height: '100%' },
              }}
              onEnd={() => setHasStrokes(!sigRef.current?.isEmpty())}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Signing as <strong className="text-slate-700">{agreement.clientName}</strong> ({agreement.clientEmail})
          </p>

          {submitError && (
            <div className="mt-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">
              {submitError}
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !hasStrokes}
              className="px-5 py-2.5 rounded-md text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand }}
            >
              {submitting ? 'Submitting…' : 'Submit Signature'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-xs text-slate-400 text-center mt-8">
          Powered by Flowspect
        </p>
      </div>
    </div>
  );
};

export default SigningPage;
